import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { NotificationBell } from "../../../assets/icon";
import useRequest from "../../../components/hooks/use-req";
import EditProfileModal from "../dashboard/edit-profile-modal";
import ChangePasswordModal from "./change-password-modal";

interface NotificationRowProps {
    title: string;
    description: string;
    enabled: boolean;
    onToggle: () => void;
}

const NotificationRow: React.FC<NotificationRowProps> = ({ title, description, enabled, onToggle }) => (
    <div className="rounded-2xl bg-gray-50 px-5 py-4 flex items-start justify-between gap-4">
        <div>
            <h4 className="font-bold text-gray-900 text-base leading-tight">{title}</h4>
            <p className="text-sm text-gray-500 mt-1 leading-relaxed">{description}</p>
        </div>

        <button
            type="button"
            role="switch"
            aria-checked={enabled}
            onClick={onToggle}
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition-colors duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2
        ${enabled ? "bg-emerald-500" : "bg-gray-300"}`}
        >
            <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-md transition-transform duration-300
          ${enabled ? "translate-x-6" : "translate-x-1"}`}
            />
        </button>
    </div>
);

const VerifiedBadge = () => (
    <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600 flex-shrink-0">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#00A86B" />
            <path d="M8 12.5L10.5 15L16 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Verified
    </span>
);

const formatDob = (iso: string) => {
    const date = new Date(iso);
    if (isNaN(date.getTime())) return iso;
    return `${date.getDate()} ${date.toLocaleString("en-US", { month: "short" })}, ${date.getFullYear()}`;
};

const Settings: React.FC = () => {
    const authToken = localStorage.getItem("authToken");

    const [whatsappNewReward, setWhatsappNewReward] = useState(false);
    const [whatsappLinkClicks, setWhatsappLinkClicks] = useState(false);
    const [emailNewReward, setEmailNewReward] = useState(false);
    const [emailLinkClicks, setEmailLinkClicks] = useState(false);

    const [profile, setProfile] = useState<any>(null);
    const [isEditProfileOpen, setIsEditProfileOpen] = useState(false);
    const [isChangePasswordOpen, setIsChangePasswordOpen] = useState(false);

    const { makeRequest: fetchProfile } = useRequest("/auth/auth/me", "GET", {
        Authorization: authToken ? `Bearer ${authToken}` : undefined,
    });

    const loadProfile = async () => {
        const [response] = await fetchProfile();
        setProfile(response?.data || response?.data?.user || null);
    };

    useEffect(() => {
        loadProfile();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className=" flex items-start justify-center px-4">
            <div className="w-full space-y-8">

                <div className='mb-10'>
                    <h1 className="text-2xl lg:text-3xl flex gap-3 font-semibold text-black">Settings
                    </h1>
                    <p className="text-[#717171] text-[18px]">   Manage your preferences and account settings</p>
                </div>

                {/* Profile Settings Card */}
                <div className="rounded-3xl bg-white border border-gray-100 shadow-xl shadow-gray-100/60 p-6 space-y-5">
                    <div>
                        <h2 className="font-bold text-gray-800 text-lg leading-tight">Profile Settings</h2>
                        <p className="text-sm text-gray-400 mt-0.5">Update your profile settings</p>
                    </div>

                    <div className="border-t border-gray-100" />

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                        {/* Email */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-1.5">Email Address</p>
                            <div className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3">
                                <span className="text-sm text-gray-500 truncate">{profile?.email || "—"}</span>
                                {profile?.email_verified && <VerifiedBadge />}
                            </div>
                            <button
                                onClick={() => setIsEditProfileOpen(true)}
                                className="text-xs font-semibold text-gray-700 underline underline-offset-2 mt-1.5"
                            >
                                Edit Email
                            </button>
                        </div>

                        {/* Password */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-1.5">Password</p>
                            <div className="rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3">
                                <span className="text-sm text-gray-400 tracking-widest">****************</span>
                            </div>
                            <button
                                onClick={() => setIsChangePasswordOpen(true)}
                                className="text-xs font-semibold text-gray-700 underline underline-offset-2 mt-1.5"
                            >
                                Update Password
                            </button>
                        </div>

                        {/* WhatsApp */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-1.5">WhatsApp Phone Number</p>
                            <div className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3">
                                <span className="text-sm text-gray-500 truncate">{profile?.phone || "—"}</span>
                                {profile?.phone_verified && <VerifiedBadge />}
                            </div>
                        </div>

                        {/* BVN */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-1.5">BVN</p>
                            <div className="flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3">
                                <span className="text-sm text-gray-500 truncate">{profile?.bvn || "—"}</span>
                                {profile?.is_verified && <VerifiedBadge />}
                            </div>
                        </div>

                        {/* DOB */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-1.5">DOB</p>
                            <button
                                onClick={() => setIsEditProfileOpen(true)}
                                className="w-full flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3 text-left hover:bg-gray-100 transition-colors"
                            >
                                <span className="text-sm text-gray-500">
                                    {profile?.date_of_birth ? formatDob(profile.date_of_birth) : "Not set"}
                                </span>
                                <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                            </button>
                        </div>

                        {/* State */}
                        <div>
                            <p className="text-sm font-semibold text-gray-700 mb-1.5">State</p>
                            <button
                                onClick={() => setIsEditProfileOpen(true)}
                                className="w-full flex items-center justify-between gap-2 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3 text-left hover:bg-gray-100 transition-colors"
                            >
                                <span className="text-sm text-gray-500">{profile?.state || "Not set"}</span>
                                <ChevronDown size={14} className="text-gray-400 flex-shrink-0" />
                            </button>
                        </div>
                    </div>
                </div>

                {/* Notification Card */}
                <div className="rounded-3xl bg-white backdrop-blur-sm border border-white shadow-xl shadow-gray-100/60 p-6 space-y-5">

                    {/* Card Header */}
                    <div className="flex items-start gap-3">
                        <div className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-xl">
                            <NotificationBell />
                        </div>

                        <div>
                            <h2 className="font-bold text-gray-800 text-lg leading-tight">
                                Notification settings
                            </h2>
                            <p className="text-sm text-gray-400 mt-0.5">
                                Manage how you receive updates about your VouchLinks activity
                            </p>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="border-t border-gray-100" />

                    {/* WhatsApp Notifications */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-800 mb-3">WhatsApp Notifications</h3>
                        <div className="space-y-3">
                            <NotificationRow
                                title="New reward"
                                description="Receive instant WhatsApp alerts when you earn a new reward."
                                enabled={whatsappNewReward}
                                onToggle={() => setWhatsappNewReward((prev) => !prev)}
                            />
                            <NotificationRow
                                title="Link Clicks"
                                description="Receive real time updates when people click your VouchLinks."
                                enabled={whatsappLinkClicks}
                                onToggle={() => setWhatsappLinkClicks((prev) => !prev)}
                            />
                        </div>
                    </div>

                    {/* Email Notifications */}
                    <div>
                        <h3 className="text-sm font-bold text-gray-800 mb-3">Email Notifications</h3>
                        <div className="space-y-3">
                            <NotificationRow
                                title="New reward"
                                description="Receive an email summary whenever a new reward is earned or paid out."
                                enabled={emailNewReward}
                                onToggle={() => setEmailNewReward((prev) => !prev)}
                            />
                            <NotificationRow
                                title="Link Clicks"
                                description="Receive email updates about activity on your shared VouchLinks."
                                enabled={emailLinkClicks}
                                onToggle={() => setEmailLinkClicks((prev) => !prev)}
                            />
                        </div>
                    </div>
                </div>

            </div>

            <EditProfileModal
                isOpen={isEditProfileOpen}
                onClose={() => setIsEditProfileOpen(false)}
                onUpdated={loadProfile}
            />

            <ChangePasswordModal
                isOpen={isChangePasswordOpen}
                onClose={() => setIsChangePasswordOpen(false)}
            />
        </div>
    );
};

export default Settings;
